import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Paper,
  Chip,
  Avatar,
  Button,
  IconButton,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  useTheme,
  alpha,
} from "@mui/material";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import QRCode from "qrcode";

import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import Nodata from "../../assets/errors/nodata.png";
import logo from "../../assets/logo.png";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";

const STAFF_IMAGE_BASE = "https://mindsinaction.com.na/assets/media/pictures/";
const ASSET_IMAGE_BASE = "https://mindsinaction.com.na/assets/verify/pictures/";

// Conditions excluded from every financial total and from the accounting export.
const NON_CAPITAL_CONDITIONS = ["discarded", "damaged", "refurbished"];

const ALL = "__all__";

const TYPES = ["purchased", "donated", "constructed"];

const CATEGORIES = [
  "Land & Buildings",
  "Electronics",
  "Furniture",
  "Office Supplies",
  "Vehicle",
  "Tools & Equipment",
  "Others",
];

const CATEGORY_TYPES = [
  "Laptop",
  "Desktop",
  "Monitor",
  "Printer",
  "Chair",
  "Table",
  "Stationery",
  "Car",
  "Van",
  "Truck",
  "Buss",
  "Power Tools",
  "Hand Tools",
  "Robotics Kit",
  "Electronics Kit",
  "Twin Science Kit",
  "Mechatronics Kit",
  "Charger",
  "Others",
];

const CONDITIONS = ["New", "Used", "Refurbished", "Damaged", "Discarded"];

const VERIFICATION_PERIODS = ["weekly", "monthly", "yearly"];

const DEPRECIATION_METHODS = [
  { value: "straight_line", label: "Straight line" },
  { value: "declining_balance", label: "Declining balance" },
  { value: "sum_of_years_digits", label: "Sum of years' digits" },
  { value: "units_of_production", label: "Units of production" },
];

const DELIVERED_STATUSES = [
  { value: "1", label: "Not delivered" },
  { value: "2", label: "Partially delivered" },
  { value: "3", label: "Fully delivered" },
];

const STATUS_TEXT = { 0: "Available", 1: "Unavailable", 2: "Damaged" };

/* ─── Helpers ──────────────────────────────────────────────────────────── */

const today = () => new Date().toISOString().split("T")[0];

const num = (v) => parseFloat(v) || 0;

const money = (v) =>
  `N$ ${num(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const compactMoney = (v) =>
  `N$ ${num(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isCapitalised = (item) =>
  !NON_CAPITAL_CONDITIONS.includes(
    String(item?.product_condition || "").toLowerCase()
  );

const statusText = (status) => STATUS_TEXT[Number(status)] || "Available";

const isVerifiedFlag = (v) => String(v) === "0";

const EMPTY_FORM = {
  invoiceNumber: "",
  sku: "",
  type: "purchased",
  description: "",
  category: "",
  categoryType: "",
  verification_period: "weekly",
  location: "Windhoek STEAM Centre",
  serialNumber: "",
  purchaseddate: today(),
  suplier: "",
  quantity: "0",
  deliveredDate: "",
  currentPeriodEnd: "",
  lifeSpanPeriod: "10",
  broaght_in_use: "",
  depreciationMethod: "straight_line",
  depreciationRate: "10",
  costprice: "0",
  additions: "0",
  actualValue: "0",
  salvageValue: "0",
  depreciationValue: "0",
  opening_depr: "0",
  current_period_depr: "0",
  accumulated_depr: "0",
  carrying_value: "0",
  book_value: "0",
  product_condition: "New",
  deliveredStatus: "1",
  comment: "",
  assignedTo: "",
  isVerified: "1",
  verifiedDate: today(),
  status: "0",
};

/* ─── Presentational components ────────────────────────────────────────── */
// Defined at module scope on purpose. A component declared inside Inventory
// would be a brand-new type on every render, so React would unmount and remount
// these subtrees on each keystroke and the focused input would lose focus after
// one character.

const FormSection = ({ ui, title, hint, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="h6"
      sx={{
        mb: hint ? 0.35 : 1.5,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        fontWeight: 700,
        color: ui.accent,
      }}
    >
      {title}
    </Typography>
    {hint && (
      <Typography variant="h6" sx={{ mb: 1.5, color: ui.textSecondary }}>
        {hint}
      </Typography>
    )}
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const StatCard = ({ ui, icon, label, value, sub, tone }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.25,
      height: "100%",
      borderRadius: 3,
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      boxShadow: ui.shadow,
      borderLeft: `4px solid ${tone}`,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          color: tone,
          bgcolor: alpha(tone, 0.14),
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h6"
        sx={{
          letterSpacing: 0.6,
          textTransform: "uppercase",
          fontWeight: 700,
          color: ui.textSecondary,
        }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography
      variant="h3"
      sx={{ fontWeight: 800, color: ui.textPrimary, lineHeight: 1.2 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="h6" sx={{ mt: 0.35, color: ui.textSecondary }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

/* ─────────────────────────────────────────────────────────────────────── */

const Inventory = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { getData, addData, updateData, deleteData } = useAuth();

  const [items, setItems] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [categoryFilter, setCategoryFilter] = useState(ALL);
  const [conditionFilter, setConditionFilter] = useState(ALL);
  const [locationFilter, setLocationFilter] = useState(ALL);
  const [showAllColumns, setShowAllColumns] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const isEditing = Boolean(selectedItem);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  /* ---------------- Data ---------------- */
  const fetchData = useCallback(async () => {
    try {
      // One inventory call - the capitalised subset is derived, not re-fetched.
      const [inventoryResponse, staffsResponse] = await Promise.all([
        getData("proccess.php", "inventory"),
        getData("proccess.php", "staffs"),
      ]);

      if (inventoryResponse.success) {
        setItems(
          Array.isArray(inventoryResponse.data) ? inventoryResponse.data : []
        );
      } else {
        toast.error(inventoryResponse.message || "Failed to load asset data");
      }

      if (staffsResponse.success && Array.isArray(staffsResponse.data)) {
        setStaffs(staffsResponse.data);
      }
    } catch (err) {
      toast.error("Network error: Failed to fetch asset data");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const staffById = useCallback(
    (id) => staffs.find((s) => String(s.Id) === String(id)),
    [staffs]
  );

  const staffName = useCallback(
    (id) => {
      const s = staffById(id);
      return s ? `${s.FirstName || ""} ${s.LastName || ""}`.trim() : "";
    },
    [staffById]
  );

  /* ---------------- Filter options ---------------- */
  const filterOptions = useMemo(() => {
    const categories = new Set();
    const conditions = new Set();
    const locations = new Set();
    items.forEach((i) => {
      if (i.category) categories.add(i.category);
      if (i.product_condition) conditions.add(i.product_condition);
      if (i.location) locations.add(i.location);
    });
    const sorted = (s) => [...s].sort((a, b) => a.localeCompare(b));
    return {
      categories: sorted(categories),
      conditions: sorted(conditions),
      locations: sorted(locations),
    };
  }, [items]);

  const activeFilterCount = [
    statusFilter,
    categoryFilter,
    conditionFilter,
    locationFilter,
  ].filter((v) => v !== ALL).length;

  const clearFilters = () => {
    setStatusFilter(ALL);
    setCategoryFilter(ALL);
    setConditionFilter(ALL);
    setLocationFilter(ALL);
  };

  /* ---------------- Filtering ---------------- */
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();

    return items
      .filter((i) => statusFilter === ALL || String(i.status) === statusFilter)
      .filter((i) => categoryFilter === ALL || i.category === categoryFilter)
      .filter(
        (i) => conditionFilter === ALL || i.product_condition === conditionFilter
      )
      .filter((i) => locationFilter === ALL || i.location === locationFilter)
      .filter((i) => {
        if (!q) return true;
        return [
          i.sku,
          i.description,
          i.serialNumber,
          i.invoiceNumber,
          i.category,
          i.categoryType,
          i.location,
          i.suplier,
          staffName(i.assignedTo),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
  }, [
    items,
    search,
    statusFilter,
    categoryFilter,
    conditionFilter,
    locationFilter,
    staffName,
  ]);

  // Financial totals ignore discarded / damaged / refurbished stock, and follow
  // whatever the filters are currently showing.
  const capitalisedVisible = useMemo(
    () => visibleItems.filter(isCapitalised),
    [visibleItems]
  );

  const totals = useMemo(() => {
    const sum = (key) =>
      capitalisedVisible.reduce(
        (acc, i) => acc + num(i[key]) * (parseInt(i.quantity, 10) || 0),
        0
      );
    return {
      count: capitalisedVisible.length,
      units: capitalisedVisible.reduce(
        (acc, i) => acc + (parseInt(i.quantity, 10) || 0),
        0
      ),
      costprice: sum("costprice"),
      actualValue: sum("actualValue"),
      bookValue: sum("book_value"),
    };
  }, [capitalisedVisible]);

  /* ---------------- SKU + QR ---------------- */
  const generateSKU = useCallback(() => {
    const prefix = "STEAM";
    if (!items || items.length === 0) return `${prefix}000001`;

    const numbers = items
      .map((item) => item.sku)
      .filter(Boolean)
      .map((sku) => parseInt(String(sku).replace(prefix, ""), 10))
      .filter((n) => !Number.isNaN(n));

    const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
    return `${prefix}${String(maxNumber + 1).padStart(6, "0")}`;
  }, [items]);

  const downloadSkuQR = async (item) => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const width = 400;
      const height = 500;
      canvas.width = width;
      canvas.height = height;

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);

      const logoImg = new Image();
      logoImg.src = logo;

      logoImg.onload = async () => {
        const logoSize = 305;
        ctx.drawImage(logoImg, (width - logoSize) / 2, 20, logoSize, 75);

        const qrDataUrl = await QRCode.toDataURL(item.sku, {
          margin: 1,
          width: 250,
        });

        const qrImg = new Image();
        qrImg.src = qrDataUrl;

        qrImg.onload = () => {
          const qrSize = 250;
          ctx.drawImage(qrImg, (width - qrSize) / 2, 120, qrSize, qrSize);

          ctx.fillStyle = "#000";
          ctx.font = "bold 28px Arial";
          ctx.textAlign = "center";
          ctx.fillText(item.sku, width / 2, 430);

          const link = document.createElement("a");
          link.download = `${item.sku}_qr.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
        };
      };
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate QR code");
    }
  };

  /* ---------------- Dialog handlers ---------------- */
  const handleAddClick = () => {
    setSelectedItem(null);
    setFormData({ ...EMPTY_FORM, sku: generateSKU() });
    setOpenDialog(true);
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setFormData(
      Object.keys(EMPTY_FORM).reduce((acc, key) => {
        acc[key] = item[key] ?? EMPTY_FORM[key];
        return acc;
      }, {})
    );
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setFormData(EMPTY_FORM);
  };

  // Clicking the backdrop must not discard a part-filled asset record — the
  // dialog only closes via Cancel, Escape, or a successful save.
  const handleDialogClose = (_event, reason) => {
    if (reason === "backdropClick") return;
    handleCloseDialog();
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      // status is stored inverted: "0" means Available.
      [name]: type === "checkbox" ? (checked ? "0" : "1") : value,
    }));
  };

  /* ---------------- Save ---------------- */
  const validate = () => {
    const required = [
      ["description", "Description"],
      ["invoiceNumber", "Invoice number"],
      ["category", "Category"],
      ["categoryType", "Category type"],
      ["location", "Location"],
      ["suplier", "Supplier"],
      ["purchaseddate", "Purchased date"],
      ["product_condition", "Condition"],
    ];
    for (const [key, label] of required) {
      if (!String(formData[key] ?? "").trim()) {
        toast.error(`${label} is required`);
        return false;
      }
    }
    if (num(formData.costprice) <= 0) {
      toast.error("Cost price must be greater than zero");
      return false;
    }
    if (num(formData.lifeSpanPeriod) <= 0) {
      toast.error("Life span period must be greater than zero");
      return false;
    }
    return true;
  };

  // NOTE: this reproduces the existing depreciation arithmetic exactly. It is
  // built into a fresh payload rather than mutating formData in place.
  const buildPayload = () => {
    const costprice = num(formData.costprice);
    const rate = num(formData.depreciationRate);
    const lifeSpan = num(formData.lifeSpanPeriod) || 1;

    const salvageValue = costprice - costprice * (rate / 100) || 0;
    const depreciationValue = (costprice - salvageValue) / lifeSpan;

    let actualValue;
    if (isEditing) {
      actualValue =
        num(formData.actualValue) <= 0
          ? costprice
          : num(formData.actualValue) - depreciationValue;
    } else {
      actualValue = costprice;
    }

    return {
      ...formData,
      salvageValue,
      depreciationValue,
      actualValue,
    };
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    const payload = buildPayload();
    setSaving(true);

    try {
      const response = isEditing
        ? await updateData("proccess.php", "inventory", payload, selectedItem.id)
        : await addData("proccess.php", "inventory", payload);

      if (!response.success) {
        toast.error(response.message || "Operation failed");
        return; // keep the dialog open so the captured input is not lost
      }

      if (Array.isArray(response.data)) {
        setItems(response.data);
      } else if (isEditing) {
        setItems((prev) =>
          prev.map((i) =>
            String(i.id) === String(selectedItem.id) ? { ...i, ...payload } : i
          )
        );
      } else {
        await fetchData();
      }

      toast.success(isEditing ? "Asset updated" : "Asset added");
      handleCloseDialog();
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData(
        "proccess.php",
        "inventory",
        itemToDelete.id
      );
      if (response.success) {
        toast.success("Asset deleted");
        if (Array.isArray(response.data)) setItems(response.data);
        else
          setItems((prev) =>
            prev.filter((i) => String(i.id) !== String(itemToDelete.id))
          );
      } else {
        toast.error(response.message || "Failed to delete asset");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete asset");
      console.error("Delete error:", err);
    }
    setItemToDelete(null);
  };

  /* ---------------- Export ---------------- */
  const exportToExcel = () => {
    const assets = capitalisedVisible;

    if (assets.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const sumOf = (key) =>
        assets.reduce((sum, item) => sum + num(item[key]), 0);

      const grand = {
        costprice: sumOf("costprice"),
        additions: sumOf("additions"),
        actualValue: sumOf("actualValue"),
        salvageValue: sumOf("salvageValue"),
        depreciationValue: sumOf("depreciationValue"),
        opening_depr: sumOf("opening_depr"),
        current_period_depr: sumOf("current_period_depr"),
        accumulated_depr: sumOf("accumulated_depr"),
        carrying_value: sumOf("carrying_value"),
        book_value: sumOf("book_value"),
      };

      const categoryTotals = assets.reduce((groups, item) => {
        const category = item.category || "Uncategorized";
        if (!groups[category]) {
          groups[category] = {
            count: 0,
            costprice: 0,
            additions: 0,
            actualValue: 0,
            depreciationValue: 0,
            accumulated_depr: 0,
            book_value: 0,
          };
        }
        const g = groups[category];
        g.count += 1;
        g.costprice += num(item.costprice);
        g.additions += num(item.additions);
        g.actualValue += num(item.actualValue);
        g.depreciationValue += num(item.depreciationValue);
        g.accumulated_depr += num(item.accumulated_depr);
        g.book_value += num(item.book_value);
        return groups;
      }, {});

      let html = `
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * { font-family: 'Segoe UI', Arial, sans-serif; box-sizing: border-box; }
            body { padding: 20px; background-color: #f5f5f5; }
            .container { background-color: white; border-radius: 8px; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
            th { background-color: #2C3E50; color: white; padding: 12px 8px; text-align: center;
                 font-weight: bold; font-size: 12px; border: 1px solid #34495E; }
            td { padding: 8px; border: 1px solid #BDC3C7; font-size: 11px; }
            tr:nth-child(even) { background-color: #F8F9FA; }
            .category-header td { background-color: #34495E; color: white; font-size: 14px; font-weight: bold; padding: 10px; }
            .totals-row td { background-color: #F39C12; color: white; font-weight: bold; font-size: 13px; border-color: #E67E22; }
            .category-totals td { background-color: #ECF0F1; font-weight: bold; font-size: 12px; border-top: 2px solid #3498DB; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .positive-value { color: #27AE60; font-weight: bold; }
            .negative-value { color: #E74C3C; font-weight: bold; }
            .report-footer { margin-top: 30px; padding: 15px; background-color: #ECF0F1;
                             border-radius: 8px; font-size: 11px; color: #7F8C8D; text-align: center; }
            .asset-badge { display: inline-block; background-color: #3498DB; color: white;
                           padding: 3px 10px; border-radius: 12px; font-size: 11px; margin-left: 10px; }
          </style>
        </head>
        <body>
          <div class="container">
            <table>
              <thead>
                <tr>
                  <th rowspan="2">ID</th>
                  <th rowspan="2">Invoice #</th>
                  <th rowspan="2">SKU</th>
                  <th rowspan="2">Type</th>
                  <th rowspan="2">Description</th>
                  <th rowspan="2">Category</th>
                  <th rowspan="2">Category Type</th>
                  <th rowspan="2">Location</th>
                  <th rowspan="2">Serial #</th>
                  <th rowspan="2">Purchased Date</th>
                  <th rowspan="2">Supplier</th>
                  <th rowspan="2">Qty</th>
                  <th rowspan="2">Life Span</th>
                  <th rowspan="2">Depreciation Method</th>
                  <th rowspan="2">Dep. Rate (%)</th>
                  <th colspan="10" style="background-color: #27AE60;">Financial Values (N$)</th>
                  <th rowspan="2">Condition</th>
                  <th rowspan="2">Status</th>
                  <th rowspan="2">Assigned To</th>
                  <th rowspan="2">Verified</th>
                  <th rowspan="2">Date Created</th>
                </tr>
                <tr>
                  <th style="background-color: #27AE60;">Cost Price</th>
                  <th style="background-color: #27AE60;">Additions</th>
                  <th style="background-color: #27AE60;">Actual Value</th>
                  <th style="background-color: #27AE60;">Salvage Value</th>
                  <th style="background-color: #27AE60;">Depreciation Value</th>
                  <th style="background-color: #27AE60;">Opening Depr</th>
                  <th style="background-color: #27AE60;">Current Depr</th>
                  <th style="background-color: #27AE60;">Accumulated Depr</th>
                  <th style="background-color: #27AE60;">Carrying Value</th>
                  <th style="background-color: #27AE60;">Book Value</th>
                </tr>
              </thead>
              <tbody>
      `;

      for (const category of Object.keys(categoryTotals).sort()) {
        const catAssets = assets.filter(
          (item) => (item.category || "Uncategorized") === category
        );
        const catTotal = categoryTotals[category];

        html += `
          <tr class="category-header">
            <td colspan="38">${category.toUpperCase()}
              <span class="asset-badge">${catTotal.count} Assets</span>
            </td>
          </tr>
        `;

        catAssets.forEach((item) => {
          const rate = num(item.depreciationRate);
          const costPrice = num(item.costprice);
          const bookValue = num(item.book_value);
          const isHighValue = bookValue > 10000;
          const isDepreciated = bookValue < costPrice * 0.2;
          const life = num(item.lifeSpanPeriod);

          html += `
            <tr style="${isHighValue ? "background-color: #FFF9C4;" : ""}">
              <td class="text-center">${item.id || "-"}</td>
              <td>${item.invoiceNumber || "-"}</td>
              <td>${item.sku || "-"}</td>
              <td>${item.type || "-"}</td>
              <td>${item.description || "-"}</td>
              <td>${item.category || "-"}</td>
              <td>${item.categoryType || "-"}</td>
              <td>${item.location || "-"}</td>
              <td>${item.serialNumber || "-"}</td>
              <td class="text-center">${fmtDate(item.purchaseddate)}</td>
              <td>${item.suplier || "-"}</td>
              <td class="text-center">${item.quantity || 0}</td>
              <td class="text-center">${life} ${life === 1 ? "Year" : "Years"}</td>
              <td>${item.depreciationMethod || "-"}</td>
              <td class="text-right ${rate > 20 ? "positive-value" : ""}">${rate}%</td>
              <td class="text-right">${money(costPrice)}</td>
              <td class="text-right">${money(item.additions)}</td>
              <td class="text-right">${money(item.actualValue)}</td>
              <td class="text-right">${money(item.salvageValue)}</td>
              <td class="text-right">${money(item.depreciationValue)}</td>
              <td class="text-right">${money(item.opening_depr)}</td>
              <td class="text-right">${money(item.current_period_depr)}</td>
              <td class="text-right">${money(item.accumulated_depr)}</td>
              <td class="text-right">${money(item.carrying_value)}</td>
              <td class="text-right ${isDepreciated ? "negative-value" : ""}">${money(bookValue)}</td>
              <td>${item.product_condition || "-"}</td>
              <td class="text-center">${statusText(item.status)}</td>
              <td>${staffName(item.assignedTo) || "-"}</td>
              <td class="text-center">${isVerifiedFlag(item.isVerified) ? "Yes" : "No"}</td>
              <td class="text-center">${fmtDate(item.dateCreated)}</td>
            </tr>
          `;
        });

        html += `
          <tr class="category-totals">
            <td colspan="5" style="text-align: right;">CATEGORY TOTALS &rarr;</td>
            <td colspan="2">${category}</td>
            <td colspan="8">&nbsp;</td>
            <td class="text-right">${money(catTotal.costprice)}</td>
            <td class="text-right">${money(catTotal.additions)}</td>
            <td class="text-right">${money(catTotal.actualValue)}</td>
            <td class="text-right">-</td>
            <td class="text-right">${money(catTotal.depreciationValue)}</td>
            <td class="text-right">-</td>
            <td class="text-right">-</td>
            <td class="text-right">${money(catTotal.accumulated_depr)}</td>
            <td class="text-right">-</td>
            <td class="text-right">${money(catTotal.book_value)}</td>
            <td colspan="7">&nbsp;</td>
          </tr>
        `;
      }

      html += `
          <tr class="totals-row">
            <td colspan="15" style="text-align: right;">GRAND TOTALS &rarr;</td>
            <td class="text-right">${money(grand.costprice)}</td>
            <td class="text-right">${money(grand.additions)}</td>
            <td class="text-right">${money(grand.actualValue)}</td>
            <td class="text-right">${money(grand.salvageValue)}</td>
            <td class="text-right">${money(grand.depreciationValue)}</td>
            <td class="text-right">${money(grand.opening_depr)}</td>
            <td class="text-right">${money(grand.current_period_depr)}</td>
            <td class="text-right">${money(grand.accumulated_depr)}</td>
            <td class="text-right">${money(grand.carrying_value)}</td>
            <td class="text-right">${money(grand.book_value)}</td>
            <td colspan="7">&nbsp;</td>
          </tr>
          </tbody>
          </table>

          <div class="report-footer">
            <strong>Report Notes:</strong><br/>
            &bull; All financial values are in Namibian Dollars (N$)<br/>
            &bull; Discarded, damaged and refurbished assets are excluded<br/>
            &bull; Yellow rows are high-value assets (Book Value &gt; N$10,000)<br/>
            &bull; Generated by the Asset Management System &mdash; ${new Date().toLocaleString()}
          </div>
          </div>
        </body>
        </html>
      `;

      const blob = new Blob([html], { type: "application/vnd.ms-excel" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `inventory_report_${new Date().toISOString().split("T")[0]}.xls`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(
        `Exported ${assets.length} asset(s) across ${
          Object.keys(categoryTotals).length
        } categories`
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  /* ---------------- Shared style helpers ---------------- */
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      color: ui.textPrimary,
      bgcolor: ui.surfaceAlt,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: alpha(ui.accent, 0.5) },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
    "& .MuiFormHelperText-root": { color: ui.textSecondary },
  };

  const primaryBtn = {
    px: 2.75,
    py: 1.1,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color: "#ffffff",
    background: ui.gradient,
    boxShadow: `0 10px 24px -12px ${alpha(ui.accent, 0.9)}`,
    "&:hover": { filter: "brightness(1.06)" },
  };

  const outlineBtn = (color) => ({
    px: 2.5,
    py: 1.05,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
    "&:hover": { bgcolor: alpha(color, 0.1), borderColor: color },
  });

  const moneyCell = (value, tone) => (
    <Typography
      variant="h6"
      sx={{
        width: "100%",
        textAlign: "right",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: num(value) > 0 ? tone : ui.textSecondary,
      }}
    >
      {num(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}
    </Typography>
  );

  /* ---------------- Columns ---------------- */
  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 70 },
      { field: "sku", headerName: "SKU", width: 130 },
      { field: "description", headerName: "Description", width: 220 },
      { field: "category", headerName: "Category", width: 150 },
      { field: "categoryType", headerName: "Category type", width: 150 },
      { field: "type", headerName: "Type", width: 120 },
      { field: "location", headerName: "Location", width: 170 },
      { field: "serialNumber", headerName: "Serial number", width: 150 },
      { field: "invoiceNumber", headerName: "Invoice #", width: 130 },
      { field: "suplier", headerName: "Supplier", width: 150 },
      {
        field: "quantity",
        headerName: "Qty",
        width: 80,
        renderCell: (p) => p.row.quantity || "0",
      },
      {
        field: "purchaseddate",
        headerName: "Purchased",
        width: 130,
        renderCell: (p) => fmtDate(p.row.purchaseddate),
      },
      {
        field: "costprice",
        headerName: "Cost price (N$)",
        width: 140,
        renderCell: (p) => moneyCell(p.row.costprice, colors.orange[400]),
      },
      {
        field: "additions",
        headerName: "Additions (N$)",
        width: 140,
        renderCell: (p) => moneyCell(p.row.additions, ui.textPrimary),
      },
      {
        field: "actualValue",
        headerName: "Closing value (N$)",
        width: 160,
        renderCell: (p) => moneyCell(p.row.actualValue, colors.indigo[400]),
      },
      {
        field: "book_value",
        headerName: "Book value (N$)",
        width: 150,
        renderCell: (p) => moneyCell(p.row.book_value, colors.green[500]),
      },
      {
        field: "product_condition",
        headerName: "Condition",
        width: 130,
        renderCell: (p) => {
          const v = String(p.row.product_condition || "");
          const tone = NON_CAPITAL_CONDITIONS.includes(v.toLowerCase())
            ? colors.red[500]
            : colors.green[500];
          return (
            <Chip
              size="small"
              label={v || "—"}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.68rem",
                color: tone,
                bgcolor: alpha(tone, 0.14),
              }}
            />
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (p) => {
          const tone = {
            0: colors.green[500],
            1: colors.orange[500],
            2: colors.red[500],
          }[Number(p.row.status)];
          return (
            <Chip
              size="small"
              label={statusText(p.row.status)}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.68rem",
                color: tone,
                bgcolor: alpha(tone || ui.textSecondary, 0.14),
              }}
            />
          );
        },
      },
      {
        field: "assignedTo",
        headerName: "Assigned to",
        width: 170,
        renderCell: (p) => {
          const staff = staffById(p.value);
          if (!staff) {
            return (
              <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                Unassigned
              </Typography>
            );
          }
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                src={staff.image ? `${STAFF_IMAGE_BASE}${staff.image}` : ""}
                sx={{ width: 26, height: 26, fontSize: 11, fontWeight: 700 }}
              >
                {`${staff.FirstName?.[0] || ""}${staff.LastName?.[0] || ""}`}
              </Avatar>
              <Typography variant="h6" noWrap sx={{ color: ui.textPrimary }}>
                {staffName(p.value)}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "isVerified",
        headerName: "Verified",
        width: 100,
        renderCell: (p) =>
          isVerifiedFlag(p.row.isVerified) ? (
            <CheckCircleOutlineIcon sx={{ color: colors.green[500] }} />
          ) : (
            <HighlightOffIcon sx={{ color: colors.red[500] }} />
          ),
      },
      {
        field: "image",
        headerName: "Image",
        width: 90,
        sortable: false,
        renderCell: (p) =>
          p.row.image ? (
            <Avatar
              variant="rounded"
              src={`${ASSET_IMAGE_BASE}${p.row.image}`}
              sx={{ width: 34, height: 34 }}
            />
          ) : (
            <Avatar
              variant="rounded"
              sx={{ width: 34, height: 34, bgcolor: ui.surfaceAlt }}
            >
              <Inventory2OutlinedIcon
                sx={{ fontSize: 18, color: ui.textSecondary }}
              />
            </Avatar>
          ),
      },
      { field: "verification_period", headerName: "Verify period", width: 140 },
      {
        field: "deliveredStatus",
        headerName: "Delivered",
        width: 150,
        renderCell: (p) =>
          DELIVERED_STATUSES.find(
            (d) => d.value === String(p.row.deliveredStatus)
          )?.label || "Not delivered",
      },
      { field: "deliveredDate", headerName: "Delivered date", width: 140 },
      { field: "lifeSpanPeriod", headerName: "Useful life (yrs)", width: 140 },
      { field: "depreciationMethod", headerName: "Depr. method", width: 170 },
      { field: "depreciationRate", headerName: "Rate (%)", width: 100 },
      {
        field: "salvageValue",
        headerName: "Residual (N$)",
        width: 140,
        renderCell: (p) => moneyCell(p.row.salvageValue, ui.textPrimary),
      },
      {
        field: "depreciationValue",
        headerName: "Depreciation (N$)",
        width: 160,
        renderCell: (p) => moneyCell(p.row.depreciationValue, ui.textPrimary),
      },
      {
        field: "opening_depr",
        headerName: "Opening depr (N$)",
        width: 160,
        renderCell: (p) => moneyCell(p.row.opening_depr, ui.textPrimary),
      },
      {
        field: "current_period_depr",
        headerName: "Current depr (N$)",
        width: 160,
        renderCell: (p) => moneyCell(p.row.current_period_depr, ui.textPrimary),
      },
      {
        field: "accumulated_depr",
        headerName: "Accum. depr (N$)",
        width: 160,
        renderCell: (p) => moneyCell(p.row.accumulated_depr, ui.textPrimary),
      },
      {
        field: "carrying_value",
        headerName: "Carrying value (N$)",
        width: 160,
        renderCell: (p) => moneyCell(p.row.carrying_value, ui.textPrimary),
      },
      { field: "currentPeriodEnd", headerName: "Period end", width: 130 },
      { field: "broaght_in_use", headerName: "Brought in use", width: 140 },
      { field: "verifiedDate", headerName: "Verified date", width: 130 },
      { field: "comment", headerName: "Comment", width: 200 },
      { field: "dateCreated", headerName: "Created", width: 130 },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Download SKU QR">
              <IconButton
                size="small"
                onClick={() => downloadSkuQR(params.row)}
                sx={{ color: colors.green[500] }}
              >
                <QrCodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEditClick(params.row)}
                sx={{ color: ui.accent2 }}
              >
                <EditNoteOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => setItemToDelete(params.row)}
                sx={{ color: colors.red[500] }}
              >
                <DeleteForeverOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colors, ui, staffById, staffName]
  );

  // The register has ~35 columns. Only the operational ones show by default;
  // the depreciation detail is one toggle away.
  const hiddenByDefault = useMemo(
    () => ({
      verification_period: false,
      deliveredStatus: false,
      deliveredDate: false,
      lifeSpanPeriod: false,
      depreciationMethod: false,
      depreciationRate: false,
      salvageValue: false,
      depreciationValue: false,
      opening_depr: false,
      current_period_depr: false,
      accumulated_depr: false,
      carrying_value: false,
      currentPeriodEnd: false,
      broaght_in_use: false,
      verifiedDate: false,
      comment: false,
      dateCreated: false,
      type: false,
      invoiceNumber: false,
    }),
    []
  );

  const NoDataOverlay = () => (
    <Stack alignItems="center" justifyContent="center" height="100%" spacing={1}>
      <img src={Nodata} alt="No data" style={{ width: 140 }} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
        No assets found
      </Typography>
      <Typography variant="h6" sx={{ color: ui.textSecondary }}>
        {search || activeFilterCount
          ? "Nothing matches the current filters."
          : "Add an asset to begin."}
      </Typography>
    </Stack>
  );

  /* ------------------------------------------------------------------ */

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", pb: 4 }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <Container maxWidth={false} sx={{ py: 3.5 }}>
        {/* ------------------------------------------------------------ */}
        {/* Header                                                        */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.4,
                color: ui.textPrimary,
              }}
            >
              Fixed Asset Register
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              Acquisition, depreciation and custody of every capitalised asset
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search SKU, description, serial…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 280 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 19, color: ui.textSecondary }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              sx={outlineBtn(colors.green[500])}
            >
              Export
            </Button>
            <Button
              startIcon={<AddCircleOutlineOutlinedIcon />}
              onClick={handleAddClick}
              sx={primaryBtn}
            >
              Add asset
            </Button>
          </Stack>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* KPI cards                                                     */}
        {/* ------------------------------------------------------------ */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              ui={ui}
              tone={ui.accent2}
              icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />}
              label="Capitalised assets"
              value={totals.count.toLocaleString()}
              sub={`${totals.units.toLocaleString()} unit(s) in total`}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              ui={ui}
              tone={colors.orange[500]}
              icon={<PaymentsOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Total cost price"
              value={compactMoney(totals.costprice)}
              sub="Acquisition cost × quantity"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              ui={ui}
              tone={colors.indigo[500]}
              icon={<TrendingDownOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Total closing value"
              value={compactMoney(totals.actualValue)}
              sub="After depreciation to date"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              ui={ui}
              tone={colors.green[500]}
              icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Total book value"
              value={compactMoney(totals.bookValue)}
              sub="Net carrying amount"
            />
          </Grid>
        </Grid>

        {/* ------------------------------------------------------------ */}
        {/* Filters                                                       */}
        {/* ------------------------------------------------------------ */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.75 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterAltOutlinedIcon sx={{ fontSize: 18, color: ui.accent }} />
              <Typography
                variant="h6"
                sx={{
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: ui.accent,
                }}
              >
                Filters
              </Typography>
              {activeFilterCount > 0 && (
                <Chip
                  size="small"
                  label={`${activeFilterCount} active`}
                  sx={{
                    height: 20,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    color: ui.accent,
                    bgcolor: alpha(ui.accent, 0.14),
                  }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showAllColumns}
                    onChange={(e) => setShowAllColumns(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                    Show depreciation columns
                  </Typography>
                }
              />
              {activeFilterCount > 0 && (
                <Button
                  size="small"
                  startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                  onClick={clearFilters}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    color: ui.textSecondary,
                    "&:hover": { color: colors.red[500] },
                  }}
                >
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={statusFilter}
              onChange={(_, v) => v !== null && setStatusFilter(v)}
              sx={{
                flexWrap: "wrap",
                gap: 1,
                "& .MuiToggleButton-root": {
                  px: 1.75,
                  py: 0.6,
                  border: `1px solid ${ui.border} !important`,
                  borderRadius: "999px !important",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12.5,
                  color: ui.textSecondary,
                  bgcolor: ui.surfaceAlt,
                  "&.Mui-selected": {
                    color: ui.accent,
                    bgcolor: alpha(ui.accent, 0.14),
                    borderColor: `${alpha(ui.accent, 0.5)} !important`,
                  },
                },
              }}
            >
              <ToggleButton value={ALL}>All</ToggleButton>
              <ToggleButton value="0">Available</ToggleButton>
              <ToggleButton value="1">Unavailable</ToggleButton>
              <ToggleButton value="2">Damaged</ToggleButton>
            </ToggleButtonGroup>

            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  sx={fieldSx}
                >
                  <MenuItem value={ALL}>All categories</MenuItem>
                  {filterOptions.categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Condition"
                  value={conditionFilter}
                  onChange={(e) => setConditionFilter(e.target.value)}
                  sx={fieldSx}
                >
                  <MenuItem value={ALL}>All conditions</MenuItem>
                  {filterOptions.conditions.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Location"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  sx={fieldSx}
                >
                  <MenuItem value={ALL}>All locations</MenuItem>
                  {filterOptions.locations.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Stack>

          <Typography variant="h6" sx={{ mt: 1.75, color: ui.textSecondary }}>
            Showing {visibleItems.length} of {items.length} asset(s) ·{" "}
            {totals.count} capitalised in the totals above
          </Typography>
        </Paper>

        {/* ------------------------------------------------------------ */}
        {/* Register                                                      */}
        {/* ------------------------------------------------------------ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            boxShadow: ui.shadow,
            height: "calc(100vh - 300px)",
            minHeight: 420,
          }}
        >
          <DataGrid
            rows={visibleItems}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            density="comfortable"
            disableRowSelectionOnClick
            initialState={{
              columns: { columnVisibilityModel: hiddenByDefault },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            columnVisibilityModel={showAllColumns ? {} : hiddenByDefault}
            onColumnVisibilityModelChange={() => {}}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              border: "none",
              color: ui.textPrimary,
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: ui.surfaceAlt,
                borderBottom: `1px solid ${ui.border}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontSize: 11,
                color: ui.textSecondary,
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${alpha(ui.border, 0.7)}`,
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: alpha(ui.accent, 0.07),
              },
              "& .MuiDataGrid-footerContainer": {
                bgcolor: ui.surfaceAlt,
                borderTop: `1px solid ${ui.border}`,
              },
              "& .MuiDataGrid-toolbarContainer": {
                p: 1.25,
                borderBottom: `1px solid ${ui.border}`,
              },
              "& .MuiTablePagination-root": { color: ui.textSecondary },
            }}
            slots={{
              toolbar: () => (
                <Box sx={{ p: 1 }}>
                  <GridToolbarQuickFilter />
                </Box>
              ),
              noRowsOverlay: NoDataOverlay,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 400, placeholder: "Filter…" },
              },
            }}
          />
        </Paper>
      </Container>

      {/* -------------------------------------------------------------- */}
      {/* Add / Edit dialog                                               */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, color: ui.textPrimary }}
          >
            {isEditing ? "Edit asset" : "Register an asset"}
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
            SKU {formData.sku || "—"}
            {isEditing ? "" : " · generated automatically"}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: ui.border }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1.5 }}>
            <FormSection ui={ui} title="Identification">
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={8}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="SKU"
                  name="sku"
                  value={formData.sku}
                  InputProps={{ readOnly: true }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Invoice number"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Serial number"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleFormChange}
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>

            <FormSection ui={ui} title="Classification">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Type"
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                >
                  {TYPES.map((t) => (
                    <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Category type"
                  name="categoryType"
                  value={formData.categoryType}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                >
                  {CATEGORY_TYPES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Condition"
                  name="product_condition"
                  value={formData.product_condition}
                  onChange={handleFormChange}
                  required
                  helperText="Discarded / damaged / refurbished leave the totals"
                  sx={fieldSx}
                >
                  {CONDITIONS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Verification period"
                  name="verification_period"
                  value={formData.verification_period}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                >
                  {VERIFICATION_PERIODS.map((v) => (
                    <MenuItem key={v} value={v} sx={{ textTransform: "capitalize" }}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </FormSection>

            <FormSection ui={ui} title="Acquisition">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Supplier"
                  name="suplier"
                  value={formData.suplier}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Purchased date"
                  name="purchaseddate"
                  value={formData.purchaseddate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleFormChange}
                  inputProps={{ min: 0 }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cost price (N$)"
                  name="costprice"
                  value={formData.costprice}
                  onChange={handleFormChange}
                  inputProps={{ min: 0, step: "0.01" }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Additions (N$)"
                  name="additions"
                  value={formData.additions}
                  onChange={handleFormChange}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Delivered status"
                  name="deliveredStatus"
                  value={formData.deliveredStatus}
                  onChange={handleFormChange}
                  sx={fieldSx}
                >
                  {DELIVERED_STATUSES.map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Delivered date"
                  name="deliveredDate"
                  value={formData.deliveredDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>

            <FormSection
              ui={ui}
              title="Depreciation"
              hint="Residual, depreciation and closing values are recalculated on save."
            >
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Useful life"
                  name="lifeSpanPeriod"
                  value={String(formData.lifeSpanPeriod)}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((y) => (
                    <MenuItem key={y} value={String(y)}>
                      {y} {y === 1 ? "Year" : "Years"}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Depreciation method"
                  name="depreciationMethod"
                  value={formData.depreciationMethod}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                >
                  {DEPRECIATION_METHODS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rate (%)"
                  name="depreciationRate"
                  value={formData.depreciationRate}
                  onChange={handleFormChange}
                  inputProps={{ min: 0, max: 100, step: "0.01" }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Brought in use"
                  name="broaght_in_use"
                  value={formData.broaght_in_use}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Current period end"
                  name="currentPeriodEnd"
                  value={formData.currentPeriodEnd}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>

            <FormSection ui={ui} title="Custody & verification">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Assigned to"
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleFormChange}
                  sx={fieldSx}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {staffs.map((user) => (
                    <MenuItem key={user.Id} value={user.Id}>
                      {user.FirstName} {user.LastName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Verified"
                  name="isVerified"
                  value={String(formData.isVerified)}
                  onChange={handleFormChange}
                  sx={fieldSx}
                >
                  <MenuItem value="0">Yes</MenuItem>
                  <MenuItem value="1">No</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Verified date"
                  name="verifiedDate"
                  value={formData.verifiedDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleFormChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={String(formData.status) === "0"}
                      onChange={handleFormChange}
                      name="status"
                    />
                  }
                  label={
                    <Typography variant="h5" sx={{ color: ui.textPrimary }}>
                      {String(formData.status) === "0"
                        ? "Available"
                        : "Unavailable"}
                    </Typography>
                  }
                />
              </Grid>
            </FormSection>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: ui.textSecondary,
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} sx={primaryBtn}>
            {saving ? "Saving…" : isEditing ? "Update asset" : "Create asset"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Delete dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, color: ui.textPrimary }}
          >
            Delete asset
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="h5"
            sx={{ color: ui.textSecondary, lineHeight: 1.6 }}
          >
            Permanently delete{" "}
            <Box
              component="span"
              sx={{ fontWeight: 800, color: ui.textPrimary }}
            >
              {itemToDelete?.sku} — {itemToDelete?.description}
            </Box>
            ? This removes it from the register and from every total. To retire
            it while keeping the history, set its condition to Discarded instead.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setItemToDelete(null)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: ui.textSecondary,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
            sx={{
              px: 2.75,
              py: 1.1,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.red[500],
              "&:hover": { bgcolor: colors.red[600] },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;
